import sharedFacetSchema from "../../api/catalog-facets.json";
import type { CategoryId } from "../types";

export type FacetSelections = Record<string, string[]>;
export type FacetOption = { value: string; label: string };
export type FacetDefinition = { label: string; options: readonly FacetOption[]; multi?: boolean };
export type FacetField = FacetDefinition & { key: string };
export type CatalogFacetSchema = {
  schemaVersion: 1;
  categories: Record<CategoryId, readonly string[]>;
  fields: Record<string, FacetDefinition>;
};

/** The same whitelist is read by the frontend and the PHP listing API. */
export const catalogFacetSchema = sharedFacetSchema as CatalogFacetSchema;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function availableFacetFields(category: CategoryId | "all"): FacetField[] {
  if (category === "all" || !Object.hasOwn(catalogFacetSchema.categories, category)) return [];
  return catalogFacetSchema.categories[category].map((key) => ({ key, ...catalogFacetSchema.fields[key] }));
}

/** Drop unknown fields, values and stale selections from a different category. */
export function normalizeFacetSelections(category: CategoryId | "all", value: unknown): FacetSelections {
  if (!isRecord(value)) return {};
  const result: FacetSelections = {};
  for (const field of availableFacetFields(category)) {
    if (!Object.hasOwn(value, field.key) || !Array.isArray(value[field.key])) continue;
    const supplied: unknown[] = value[field.key] as unknown[];
    const selected = field.options.filter((option) => supplied.includes(option.value)).map((option) => option.value);
    const normalized = field.multi ? selected : selected.slice(0, 1);
    if (normalized.length) result[field.key] = normalized;
  }
  if (category === "telescopes" && result.telescope_setup?.[0] === "tube_only") delete result.mount_type;
  return result;
}

export function hasFacetFilters(category: CategoryId | "all", values: unknown): boolean {
  return Object.keys(normalizeFacetSelections(category, values)).length > 0;
}

/** These are seller-declared attributes, not an equipment compatibility verdict. */
export function facetSpecifications(category: CategoryId | "all", values: unknown): Record<string, string> {
  const normalized = normalizeFacetSelections(category, values);
  const result: Record<string, string> = {};
  for (const field of availableFacetFields(category)) {
    const selected = normalized[field.key];
    if (!selected?.length) continue;
    result[field.label] = field.options.filter((option) => selected.includes(option.value)).map((option) => option.label).join(", ");
  }
  return result;
}

function checkedValues(field: FacetDefinition, value: unknown): string[] | null {
  if (!Array.isArray(value) || value.length > (field.multi ? field.options.length : 1)) return null;
  if (!value.every((item) => typeof item === "string" && field.options.some((option) => option.value === item))) return null;
  return value;
}

/** AND between fields and requested features. Missing/unknown values never imply a match. */
export function matchingFacetSelections(listingFacets: unknown, filterFacets: unknown): boolean {
  if (!isRecord(filterFacets)) return false;
  for (const [key, requestedValue] of Object.entries(filterFacets)) {
    if (!Object.hasOwn(catalogFacetSchema.fields, key)) return false;
    const field = catalogFacetSchema.fields[key];
    const requested = checkedValues(field, requestedValue);
    if (!requested) return false;
    if (!requested.length) continue;
    if (!isRecord(listingFacets) || !Object.hasOwn(listingFacets, key)) return false;
    const declared = checkedValues(field, listingFacets[key]);
    if (!declared || !requested.every((value) => declared.includes(value))) return false;
  }
  return true;
}
